/**
 * Archiv / Papierkorb Manager
 *
 * Server ist die Quelle der Wahrheit (siehe TODO_Archiv_Backend.md Abschnitt 0).
 * Diese Datei ruft nur noch das Backend (/api/archive) auf - es wird nichts
 * mehr dauerhaft im Client gespeichert. Die Auth (JWT) übernimmt archiveApi.js
 * genau wie bei allen anderen Backend-Aufrufen der App.
 *
 * Zwei unabhängige Auslöser speisen dasselbe Archiv:
 *  1. LÖSCHEN (reason: 'deleted') – wird IMMER archiviert, unabhängig von der
 *     "Automatische Datensicherung"-Einstellung. Das ist der eigentliche
 *     Papierkorb: gelöschte Kategorien/Lesezeichen/Rezepte/Einkaufslisten
 *     können darüber wiederhergestellt werden.
 *  2. ANLEGEN (reason: 'auto-snapshot') – wird NUR archiviert, wenn
 *     "Automatische Datensicherung" (localStorage 'autoBackup') aktiviert ist.
 *     Zusätzliche Sicherheitskopie für Kategorien, Lesezeichen, Rezepte und
 *     Einkaufslisten. Das Original bleibt dabei unangetastet bestehen.
 */

import archiveApi from '../services/archiveApi';

export const ENTITY_TYPES = {
  CATEGORY: 'category',
  BOOKMARK: 'bookmark',
  RECIPE: 'recipe',
  SHOPPING_LIST: 'shoppingList'
};

export const ARCHIVE_REASONS = {
  DELETED: 'deleted',
  AUTO_SNAPSHOT: 'auto-snapshot'
};

export function isAutoBackupEnabled() {
  try {
    return localStorage.getItem('autoBackup') !== 'false';
  } catch {
    return true;
  }
}

/**
 * Legt einen Snapshot im Archiv ab (POST /api/archive).
 *
 * Fehler werden hier bewusst abgefangen und nur geloggt (nicht geworfen):
 * Ein Netzwerkfehler beim Archivieren darf die eigentliche Aktion (Löschen/
 * Anlegen des Elements) nicht blockieren - das Archivieren ist eine
 * Zusatzfunktion, kein kritischer Pfad.
 *
 * @param {string} entityType - einer von ENTITY_TYPES
 * @param {string} reason - einer von ARCHIVE_REASONS
 * @param {object} data - die vollständigen Daten des Elements (Snapshot zur Wiederherstellung)
 * @param {object} [meta] - optionale Zusatzinfos für die Anzeige, z.B. { label: 'Mein Rezept' }
 * @returns {Promise<object|null>} der angelegte Archiv-Eintrag, oder null falls nicht archiviert
 *   (z.B. weil 'auto-snapshot' bei deaktivierter Datensicherung übersprungen wurde, oder
 *   der Backend-Call fehlschlug)
 */
export async function archiveItem(entityType, reason, data, meta = {}) {
  if (!data) return null;

  // Auto-Snapshots respektieren die Einstellung; Löschungen (Papierkorb) werden immer archiviert
  if (reason === ARCHIVE_REASONS.AUTO_SNAPSHOT && !isAutoBackupEnabled()) {
    return null;
  }

  try {
    return await archiveApi.create({
      entityType,
      reason,
      originalId: data.id != null ? String(data.id) : undefined,
      label: meta.label || data.title || data.name || '',
      snapshot: data
    });
  } catch (error) {
    console.error('❌ Archivieren fehlgeschlagen (blockiert die eigentliche Aktion nicht):', error);
    return null;
  }
}

/**
 * Liest Archiv-Einträge vom Server, optional gefiltert nach Typ.
 * Liefert bei Fehlern eine leere Liste (die Archiv-Ansicht zeigt dann "keine
 * Einträge" statt einer kaputten UI - der Fehler wird geloggt).
 */
export async function getArchive(entityType = null) {
  try {
    return await archiveApi.list(entityType);
  } catch (error) {
    console.error('❌ Archiv konnte nicht geladen werden:', error);
    return [];
  }
}

/**
 * Entfernt einen Eintrag endgültig aus dem Archiv. Wirft bei Fehlern (im
 * Gegensatz zu archiveItem/getArchive bewusst), damit die aufrufende UI
 * (Archive.js) dem Nutzer ein Fehler-Feedback geben kann.
 */
export async function removeFromArchive(archiveId) {
  return archiveApi.remove(archiveId);
}

/**
 * Stellt einen Archiv-Eintrag wieder her. Legt serverseitig ein neues Objekt
 * (neue ID) an und entfernt danach automatisch den Archiv-Eintrag. Wirft bei
 * Fehlern (z.B. 409 Conflict, Name existiert bereits) - der Archiv-Eintrag
 * bleibt dann serverseitig bestehen, Nutzer kann es erneut versuchen.
 * @returns {Promise<{success: boolean, restored: object}>}
 */
export async function restoreFromArchive(archiveId) {
  return archiveApi.restore(archiveId);
}

/**
 * Lädt den gespeicherten Plain-Text-Content-Snapshot einer Entität (z.B. zum
 * Anzeigen, wenn die Originalseite verschwunden ist).
 * @returns {Promise<object|null>} null falls (noch) kein Snapshot existiert
 */
export async function getArchiveContent(entityType, entityId) {
  try {
    return await archiveApi.getContent(entityType, entityId);
  } catch (error) {
    console.error('❌ Content-Snapshot konnte nicht geladen werden:', error);
    return null;
  }
}

/**
 * Legacy-Cleanup: entfernt noch vorhandene lokale Archiv-Daten aus der
 * Übergangsphase (vor der Backend-Anbindung, Branch v0.9.14). Es gibt keinen
 * aktiven lokalen Speicherort mehr - diese Funktion räumt nur eventuelle
 * Altlasten auf. Wird weiterhin beim Logout aufgerufen (AuthContext.js), ist
 * aber unkritisch geworden, da ohnehin nichts Sensibles mehr dauerhaft lokal
 * liegt (Server ist Quelle der Wahrheit, siehe Modul-Kommentar oben).
 */
export function clearArchive(userEmail) {
  try {
    localStorage.removeItem(`myboki_archive_${userEmail || 'anonymous'}`);
  } catch {
    // localStorage nicht verfügbar - unkritisch, es gab ohnehin nichts mehr zu räumen
  }
}
